import { useContext, useEffect, useState } from "react";
import { AircraftDeliveryInfoContext } from "../../contexts/AircraftDeliveryInfoContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import MultioptionButton from "../../../../components/Buttons/MultioptionButton";
import "./AircraftDeliveryInformation.css";
import { Dropdowns } from "../../../../components/Dropdowns/Dropdowns";
import { DropdownsContext } from "../../../../contexts/DropdownsContext";
import { PartSelection } from "../../../../components/PartSelection/PartSelection";
import { use } from "react";
import Input from "../../../../components/Inputs/Input";
import BasicDatePicker from "../../../../components/Datepicker/Datepicker";
import {
  getDeliveryInformation,
  updateDeliveryInformation,
} from "../../services/AircraftConfigurationService";
import {
  localStorageGet,
  localStorageSave,
} from "../../../../services/localStorageService";
import { useSnackBar } from "../../../../hooks/useSnackBar";
import { HTTP_RESPONSE_CODES } from "../../../../globals/globals";
const localStoragePrefix = "configuration/aircraft/deliveryInfo/draft/general";
const ribbonColors = ["var(--blue-300)", "var(--green-350)", "#f7931e"];
const defaultEmptyValues = {
  deliveryDate: "",
  totalDeliveryFlightHours: "",
  totalDeliveryBlockHours: "",
  totalDeliveryFlightCycles: "",
  totalDeliveryBreaksCycles: "",
  assammbliesList: [],
};
const AircraftDeliveryInformation = ({
  aircraft,
  disabled,
  ribbon,
  open,
  numberOfEngines,
  numberOfLandingGears,
}) => {
  const generalSectionButtons = [
    {
      label: "Save and Finish",
      onClick: () => saveSection(true),
      icon: "icon-save",
      id: "save",
    },
    {
      label: "Save and Continue Editing",
      onClick: () => saveSection(false),
      icon: "icon-save",
      id: "save-continue",
    },
    {
      label: "Cancel",
      onClick: () => cancelEdit(),
      icon: "icon-cross",
      id: "cancel",
      type: "danger",
    },
  ];
  const dropdownContext = useContext(DropdownsContext);
  const [backup, setBackup] = useState(null);
  const [values, setValues] = useState({ ...defaultEmptyValues });
  const [defaults, setDefaults] = useState({});
  const doNothing = () => {};
  const { hasPermission } = usePermissions();
  const { saveDraft, removeDraft, resetDelivery, setUpdateInformation } =
    useContext(AircraftDeliveryInfoContext);
  const { successMsg, warningMsg, infoMsg } = useSnackBar();
  const cancelEdit = () => {
    removeDraft(aircraft.id, "general");
    setValues(backup ?? defaultEmptyValues);
    setDefaults(backup ?? defaultEmptyValues);
  };
  const [engines, setEngines] = useState({});
  const [landingGears, setLandingGears] = useState({});
  const [apu, setApu] = useState(null);
  useEffect(() => {
    if (disabled && typeof values.id === "number") {
      localStorageSave(localStoragePrefix, { id: aircraft.id }, values);
    }
  }, [values]);

  useEffect(() => {
    if (open) {
      getDeliveryInformation(aircraft.id)
        .then((response) => {
          if (response.data.responseCode === 200) {
            if (response.data.load != null) {
              let {
                id,
                deliveryDate,
                totalDeliveryFlightHours,
                totalDeliveryBlockHours,
                totalDeliveryFlightCycles,
                totalDeliveryBreaksCycles,
                assammbliesList,
                version,
              } = response.data.load;

              let draftValues = localStorageGet(localStoragePrefix, {
                id: aircraft.id.toString(),
              })[0];
              const transformedAssemblies =
                assammbliesList?.map((item) => ({
                  type: item.type,
                  position: item.position,
                  serializedPart: { id: item.serializedPartId },
                })) || [];
              let form = {
                ...defaultEmptyValues,
                id,
                deliveryDate: deliveryDate?.split("T")[0],
                totalDeliveryFlightHours:
                  (totalDeliveryFlightHours ?? 0) +
                  ":" +
                  String(
                    response.data.load.totalDeliveryFlightMinutes ?? 0
                  ).padStart(2, "0"),
                totalDeliveryBlockHours:
                  (totalDeliveryBlockHours ?? 0) +
                  ":" +
                  String(
                    response.data.load.totalDeliveryBlockMinutes ?? 0
                  ).padStart(2, "0"),
                totalDeliveryFlightCycles: totalDeliveryFlightCycles ?? 0,
                totalDeliveryBreaksCycles: totalDeliveryBreaksCycles ?? 0,
                assammbliesList: transformedAssemblies,
                version: version,
                aircraftId: { id: aircraft.id },
              };
              assammbliesList?.forEach((item) => {
                const serializedPart = {
                  "sp.id": item.serializedPartId,
                  part_number: item.partNumber,
                  serial_number: item.serializedNumber,
                  description: item.partDescription,
                };

                if (item.type === "Engine") {
                  setEngines((prev) => ({ ...prev, [item.position]: item }));
                } else if (item.type === "Landing Gear") {
                  setLandingGears((prev) => ({
                    ...prev,
                    [item.position]: item,
                  }));
                } else if (item.type === "Apu") {
                  setApu(serializedPart);
                }
              });
              setDefaults({ ...form, ...draftValues });
              setValues({ ...form, ...draftValues });
              setBackup(form);
            } else {
              let draftValues = localStorageGet(localStoragePrefix, {
                id: aircraft.id.toString(),
              })[0];
              let form = {
                ...defaultEmptyValues,
                aircraftId: aircraft.id,
              };
              setDefaults({ ...form, ...draftValues });
              setValues({ ...form, ...draftValues });
            }
          }
        })
        .catch((error) => {
          let form = { ...defaultEmptyValues };
          setDefaults(form);
          setValues(form);
        });
    }
  }, [open, aircraft]);

  const saveSection = (disableForm) => {
    infoMsg("Saving...");
    // let draft = JSON.parse(localStorage.getItem(`${localStoragePrefix}?id=${part.id}`) ?? "{}");
    let draft = localStorageGet(localStoragePrefix, {
      id: aircraft.id.toString(),
    })[0];

    draft.aircraftId = { id: aircraft.id };
    if (draft.deliveryDate) {
      const [year, month, day] = draft.deliveryDate.split("-");
      const date = new Date(year, month - 1, day);
      draft.deliveryDate = date;
    }
    if (!draft.assammbliesList) {
      draft.assammbliesList = [];
    }

    if (draft.totalDeliveryFlightHours) {
      const [hours, minutes] = draft.totalDeliveryFlightHours.split(":");
      draft.totalDeliveryFlightHours = parseInt(hours);
      draft.totalDeliveryFlightMinutes = parseInt(minutes) ?? 0;
    }
    if (draft.totalDeliveryBlockHours) {
      const [hours, minutes] = draft.totalDeliveryBlockHours.split(":");
      draft.totalDeliveryBlockHours = parseInt(hours);
      draft.totalDeliveryBlockMinutes = parseInt(minutes) ?? 0;
    }

    // Crear un nuevo objeto Date (recuerda que el mes en JavaScript es 0-indexado, es decir, enero es 0)

    updateDeliveryInformation(draft)
      .then((response) => {
        if (response.data.responseCode === HTTP_RESPONSE_CODES.UPDATED) {
          successMsg("Aircraft saved successfully");
          response.data.load.deliveryDate =
            response.data.load.deliveryDate?.split("T")[0];
          response.data.load.totalDeliveryBlockHours = `${
            response.data.load.totalDeliveryBlockHours
          }:${
            response.data.load.totalDeliveryBlockMinutes < 10
              ? "0" + response.data.load.totalDeliveryBlockMinutes
              : response.data.load.totalDeliveryBlockMinutes
          }`;
          response.data.load.totalDeliveryFlightHours = `${
            response.data.load.totalDeliveryFlightHours
          }:${
            response.data.load.totalDeliveryFlightMinutes < 10
              ? "0" + response.data.load.totalDeliveryFlightMinutes
              : response.data.load.totalDeliveryFlightMinutes
          }`;
          setValues({ ...defaultEmptyValues, ...response.data.load });
          setBackup({ ...defaultEmptyValues, ...response.data.load });
          setUpdateInformation(new Date());
          if (disableForm) {
            removeDraft(aircraft.id, "general");
          }
        } else if (
          response.data.responseCode === HTTP_RESPONSE_CODES.VALIDATIONS_FAILED
        ) {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`,
            "One or more fields are invalid.",
            30000
          );
        } else {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`,
            100000
          );
        }
      })
      .catch((error) => {
        if (error.status === HTTP_RESPONSE_CODES.VERSION_CONFLICT) {
          warningMsg(
            `Error`,
            "The resource has been modified by another transaction. Please refresh and try again",
            30000
          );
        } else {
          warningMsg(`Something went wrong: ${error}`);
        }
      });
  };

  const applySelectionLanding = (selectedPart, position) => {
    if (!selectedPart["sp.id"]) return;

    const newAssembly = {
      type: "Engine",
      position,
      serializedPart: { id: selectedPart["sp.id"] },
    };

    setLandingGears((prev) => ({ ...prev, [position]: selectedPart }));

    setValues((prev) => {
      const filteredList = (prev.assammbliesList || []).filter(
        (item) => !(item.position === position && item.type === "Landing Gear")
      );
      return {
        ...prev,
        assammbliesList: [...filteredList, newAssembly],
      };
    });
  };
  const applySelectionEngine = (selectedPart, position) => {
    if (!selectedPart["sp.id"]) return;

    const newAssembly = {
      type: "Engine",
      position,
      serializedPart: { id: selectedPart["sp.id"] },
    };

    setEngines((prev) => ({ ...prev, [position]: selectedPart }));

    setValues((prev) => {
      const filteredList = (prev.assammbliesList || []).filter(
        (item) => !(item.position === position && item.type === "Engine")
      );
      return {
        ...prev,
        assammbliesList: [...filteredList, newAssembly],
      };
    });
  };

  const deleteSelectionLanding = (e, position) => {
    e.stopPropagation();

    setLandingGears((prev) => {
      const updated = { ...prev };
      delete updated[position];
      return updated;
    });

    setValues((prev) => {
      const updatedList = (prev.assammbliesList || []).filter(
        (item) => !(item.position === position && item.type === "Landing Gear")
      );
      return {
        ...prev,
        assammbliesList: updatedList,
      };
    });
  };
  const deleteSelectionEngine = (e, position) => {
    e.stopPropagation();

    setEngines((prev) => {
      const updated = { ...prev };
      delete updated[position];
      return updated;
    });

    setValues((prev) => {
      const updatedList = (prev.assammbliesList || []).filter(
        (item) => !(item.position === position && item.type === "Engine")
      );
      return {
        ...prev,
        assammbliesList: updatedList,
      };
    });
  };
  const applySelection = (type, section) => {
    if (!type["sp.id"]) return;

    let newAssembly;
    let setFunction;

    switch (section) {
      case 4:
        newAssembly = {
          type: "Apu",
          position: 1,
          serializedPart: { id: type["sp.id"] },
        };
        setFunction = setApu;
        break;
      default:
        return;
    }

    setFunction(type);

    setValues((prev) => {
      const filteredList = (prev.assammbliesList || []).filter(
        (item) =>
          !(
            item.position === newAssembly.position &&
            item.type === newAssembly.type
          )
      );

      return {
        ...prev,
        assammbliesList: [...filteredList, newAssembly],
      };
    });
  };
  const deleteSelection = (e, section) => {
    e.stopPropagation();

    let positionToDelete;
    let typeToDelete;
    let clearFunction;

    switch (section) {
      case 4:
        positionToDelete = 1;
        typeToDelete = "Apu";
        clearFunction = setApu;
        break;
      default:
        return;
    }

    clearFunction(null);

    setValues((prev) => {
      const updatedList = (prev.assammbliesList || []).filter(
        (item) =>
          !(item.position === positionToDelete && item.type === typeToDelete)
      );

      return {
        ...prev,
        assammbliesList: updatedList,
      };
    });
  };

  const changeValue = (e) => {
    const { name, value, completeValue } = e.target;
    setValues((prev) => {
      return {
        ...prev,
        ...{ [name]: value },
      };
    });
  };

  const updateDropdown = (dropdownId, parentValues, dependenciesToUpdate) => {};
  const changeValueDate = (newValue, name) => {
    const completeValue = newValue?.format("YYYY-MM-DD");
    setValues((prev) => {
      return {
        ...prev,
        ...{ [name]: completeValue },
      };
    });
  };
  const resetDropdownsReload = () => {
    dropdownContext.setDropdownsData((filterState) => {
      if (filterState.length > 0) {
        return filterState.map((e) => {
          if (e.reload) {
            return { ...e, reload: false };
          }
          return e;
        });
      }
      return filterState;
    });
  };

  return (
    <div className="general-aircraft-section-form-wrapper">
      <div className="general-section-aircraft-form-component">
        <div className="general-section-aircraft-form-header">
          {ribbon && (
            <div className="ribbons-container">
              {ribbon
                .filter?.((r) => r.when)
                ?.map?.((r, i, a) => (
                  <span
                    className="ribbon"
                    key={r.label}
                    style={{
                      backgroundColor:
                        r.color ?? ribbonColors[i % ribbonColors.length],
                      left:
                        i < a.length - 1
                          ? `${-0.5 * (ribbon.length - 1 - i)}rem`
                          : "",
                    }}
                  >
                    {r.label}
                  </span>
                ))}
            </div>
          )}
          <div className="title-container">
            <b>Delivery Information</b>
          </div>
          <div className="general-info-actions">
            {!disabled && hasPermission("update") && (
              <button
                className="outlined iconTextButton small-size"
                onClick={() => saveDraft(aircraft.id, "general", values)}
              >
                <i className="icon-edit"></i>
                <span>Edit</span>
              </button>
            )}
            {disabled && hasPermission("update") && (
              <MultioptionButton buttons={generalSectionButtons} />
            )}
          </div>
        </div>
        <div
          className={`general-section-delivery-form-row-date ${
            disabled ? "row-up" : ""
          }`}
        >
          <div className="general-section-delivery-form-row-date-info">
            <div className="icon-delivery"></div>
            <p>Delivery Date</p>
          </div>
          <div className="general-section-delivery-form-row-date-date">
            {disabled ? (
              <BasicDatePicker
                style={{ gridColumn: "span 1" }}
                label={""}
                onChange={(newValue) => {
                  changeValueDate(newValue, "deliveryDate");
                }}
                isDateTime={false}
                value={values["deliveryDate"]}
                name="deliveryDate"
              ></BasicDatePicker>
            ) : (
              <>
                <b>{values["deliveryDate"] ?? "--"}</b>
                <div className="icon-date"></div>
              </>
            )}
          </div>
        </div>
        <b>Initial Flight Record</b>
        <div className="flight-status-container-body-flight-kpi">
          <div
            className={`flight-status-container-body-flight-kpi-info ${
              disabled ? "kpi-up" : ""
            }`}
          >
            <div className="icon-flight-total icon"></div>
            <div className="flight-status-container-body-flight-kpi-info-text">
              {disabled ? (
                <Input
                  style={{ gridColumn: "span 1" }}
                  name="totalDeliveryFlightHours"
                  required={true}
                  disableAutocomplete={true}
                  onChange={changeValue}
                  label={""}
                  isFlightHours={true}
                  value={values["totalDeliveryFlightHours"]}
                />
              ) : (
                <b>{values["totalDeliveryFlightHours"] ?? "0:00"}</b>
              )}

              <p>Total Flight Hours</p>
              <p>At Delivery</p>
            </div>
          </div>
          <div
            className={`flight-status-container-body-flight-kpi-info ${
              disabled ? "kpi-up" : ""
            }`}
          >
            <div className="icon-flight-hours icon"></div>
            <div className="flight-status-container-body-flight-kpi-info-text">
              {disabled ? (
                <Input
                  style={{ gridColumn: "span 1" }}
                  name="totalDeliveryBlockHours"
                  required={true}
                  disableAutocomplete={true}
                  onChange={changeValue}
                  label={""}
                  isFlightHours={true}
                  value={values["totalDeliveryBlockHours"]}
                />
              ) : (
                <b>{values["totalDeliveryBlockHours"] ?? "0:00"}</b>
              )}

              <p>Total Block Hours</p>
              <p>At Delivery</p>
            </div>
          </div>
          <div
            className={`flight-status-container-body-flight-kpi-info ${
              disabled ? "kpi-up" : ""
            }`}
          >
            <div className="icon-flight-cycles icon"></div>
            <div className="flight-status-container-body-flight-kpi-info-text">
              {disabled ? (
                <Input
                  style={{ gridColumn: "span 1" }}
                  name="totalDeliveryFlightCycles"
                  required={true}
                  disableAutocomplete={true}
                  onChange={changeValue}
                  label={""}
                  value={values["totalDeliveryFlightCycles"]}
                />
              ) : (
                <b>{values["totalDeliveryFlightCycles"] ?? "0"}</b>
              )}

              <p>Total Flight Cycles</p>
              <p>At Delivery</p>
            </div>
          </div>
          <div
            className={`flight-status-container-body-flight-kpi-info ${
              disabled ? "kpi-up" : ""
            }`}
          >
            <div className="icon-total-breaks icon"></div>
            <div className="flight-status-container-body-flight-kpi-info-text">
              {disabled ? (
                <Input
                  style={{ gridColumn: "span 1" }}
                  name="totalDeliveryBreaksCycles"
                  required={true}
                  disableAutocomplete={true}
                  onChange={changeValue}
                  label={""}
                  value={values["totalDeliveryBreaksCycles"]}
                />
              ) : (
                <b>{values["totalDeliveryBreaksCycles"] ?? "0"}</b>
              )}

              <p>Total Breaks Cycles</p>
              <p>At Delivery</p>
            </div>
          </div>
        </div>
        <b>Delivered Major Assemblies - Part Type</b>
        <div
          className={`general-section-delivery-form-row  ${
            !disabled ? "disabled-from-row" : ""
          }`}
        >
          <div className="general-section-delivery-form-row-info">
            <div className="delivery-header">
              <div className="icon-delivery-engine"></div>
              <b>Engines</b>
            </div>

            {Array.from({ length: numberOfEngines }, (_, index) => {
              const position = index + 1;
              const engine = engines[position];
              return (
                <PartSelection
                  key={position}
                  clearLabel={null}
                  updateDropdown={updateDropdown}
                  resetDropdownsReload={resetDropdownsReload}
                  onOptionSelect={(value, data) =>
                    applySelectionEngine(value, position)
                  }
                  data={dropdownContext.dropdownIdxData[47]}
                  preSelectValue={engine ? engine["sp.id"] : null}
                >
                  {engine ? (
                    <div className="general-section-delivery-nodashed-row">
                      <div className="dropdown-main-property">
                        <span>PN</span>
                            <b>{engine["part_number"] || engine["partNumber"]}</b>
                    
                      </div>
                      <div className="dropdown-main-property">
                        <span>SN</span>
                        <b>{engine["serial_number"] || engine["serializedNumber"]}</b>
                      </div>
                      <div className="dropdown-main-property">
                        <span>Description</span>
                        <b>{engine["description"] || engine["partDescription"]}</b>
                      </div>
                      <div
                        className="delete-part-button"
                        onClick={(e) => deleteSelectionEngine(e, position)}
                      >
                        <i className="icon-delete-new"></i>
                      </div>
                    </div>
                  ) : (
                    <div className="general-section-delivery-dashed-row">
                      <p>+ Add Engine {index + 1}</p>
                    </div>
                  )}
                </PartSelection>
              );
            })}
          </div>
          <div className="general-section-delivery-form-row-info">
            <div className="delivery-header">
              <div className="icon-delivery-landing"></div>
              <b>Landing Gear</b>
            </div>
            {Array.from({ length: numberOfLandingGears }, (_, index) => {
              const position = index + 1;
              const landingGear = landingGears[position];

              return (
                <PartSelection
                  key={position}
                  clearLabel={null}
                  updateDropdown={updateDropdown}
                  resetDropdownsReload={resetDropdownsReload}
                  onOptionSelect={(value, data) =>
                    applySelectionLanding(value, position)
                  }
                  data={dropdownContext.dropdownIdxData[46]}
                  preSelectValue={landingGear ? landingGear["sp.id"] : null}
                >
                  {landingGear ? (
                    <div className="general-section-delivery-nodashed-row">
                      <div className="dropdown-main-property">
                        <span>PN</span>
                        <b>{landingGear["part_number"] || landingGear["partNumber"]}</b>
                      </div>
                      <div className="dropdown-main-property">
                        <span>SN</span>
                        <b>{landingGear["serial_number"] || landingGear["serializedNumber"]}</b>
                      </div>
                      <div className="dropdown-main-property">
                        <span>Description</span>
                        <b>{landingGear["description"] || landingGear["partDescription"]}</b>
                      </div>
                      <div
                        className="delete-part-button"
                        onClick={(e) => deleteSelectionLanding(e, position)}
                      >
                        <i className="icon-delete-new"></i>
                      </div>
                    </div>
                  ) : (
                    <div className="general-section-delivery-dashed-row">
                      <p>
                        + Add {index == 0 ? " Nose Landing Gear" : " Landing Gear " +index}
                      </p>
                    </div>
                  )}
                </PartSelection>
              );
            })}
          </div>
          <div className="general-section-delivery-form-row-info">
            <div className="delivery-header">
              <div className="icon-delivery-apu"></div>
              <b>APU</b>
            </div>
            <PartSelection
              clearLabel={null}
              updateDropdown={updateDropdown}
              resetDropdownsReload={resetDropdownsReload}
              onOptionSelect={(value, data) => applySelection(value, 4)}
              data={dropdownContext.dropdownIdxData[44]}
              preSelectValue={apu ? apu["sp.id"] : null}
            >
              {apu ? (
                <div className="general-section-delivery-nodashed-row">
                  <div className="dropdown-main-property">
                    <span>PN</span>
                    <b>{apu["part_number"]}</b>
                  </div>
                  <div className="dropdown-main-property">
                    <span>SN</span>
                    <b>{apu["serial_number"]}</b>
                  </div>
                  <div className="dropdown-main-property">
                    <span>Description</span>
                    <b>{apu["description"]}</b>
                  </div>
                  <div
                    className="delete-part-button"
                    onClick={(e) => deleteSelection(e, 4)}
                  >
                    <i className="icon-delete-new"></i>
                  </div>
                </div>
              ) : (
                <div className="general-section-delivery-dashed-row">
                  <p>+ Add APU</p>
                </div>
              )}
            </PartSelection>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AircraftDeliveryInformation;
